// screens/RecordFile.tsx — C1 (2026-09-28): one kept file, whole. Download
// is the copy — there is no second "Keep a copy" control (spec). A file
// with no stored bytes says so plainly and offers no Download.
import { useEffect, useState } from 'preact/hooks';
import { ApiError, getRecordFile, getRecordFileText, openRecordFileDownload, type RecordFileApi, type RecordFileTextApi } from '../lib/api';
import { BackLink } from '../components/BackLink';
import './spread.css';
import './RecordFile.css';

export interface RecordFileProps {
  tenantId: string;
  id: string;
  onBack: () => void;
}

type LoadState = { phase: 'loading' } | { phase: 'error'; message: string } | { phase: 'ready'; file: RecordFileApi };

// C1b item 2 ("Read here") — its own load state, independent of the file record's: the pane opens
// lazily (fetched the first time it is opened, never on page load) and stays loaded across a
// close/reopen within the same visit.
type TextState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; result: RecordFileTextApi }
  | { phase: 'error'; message: string };

function sizeLabel(bytes: number | null): string | null {
  if (bytes === null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RecordFile({ tenantId, id, onBack }: RecordFileProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadNote, setDownloadNote] = useState<string | null>(null);
  const [readOpen, setReadOpen] = useState(false);
  const [textState, setTextState] = useState<TextState>({ phase: 'idle' });

  useEffect(() => {
    setLoad({ phase: 'loading' });
    setDownloadNote(null);
    setReadOpen(false);
    setTextState({ phase: 'idle' });
    getRecordFile(tenantId, id)
      .then((file) => setLoad({ phase: 'ready', file }))
      .catch((err: unknown) => {
        setLoad({ phase: 'error', message: err instanceof ApiError && err.serverMessage ? err.serverMessage : 'the record is not reachable' });
      });
  }, [tenantId, id]);

  async function download() {
    setDownloadBusy(true);
    setDownloadNote(null);
    try {
      await openRecordFileDownload(tenantId, id);
    } catch (err) {
      setDownloadNote(err instanceof ApiError && err.serverMessage ? err.serverMessage : 'the download could not be reached');
    } finally {
      setDownloadBusy(false);
    }
  }

  // Lazy: fetched the first time the pane opens, never on page load — closing and reopening within
  // the same visit does not refetch. A failed fetch leaves the pane open with the error sentence,
  // so pressing the button again (which re-enters this branch, since textState is still not
  // 'ready') retries.
  async function toggleRead() {
    if (readOpen) {
      setReadOpen(false);
      return;
    }
    setReadOpen(true);
    if (textState.phase === 'ready') return;
    setTextState({ phase: 'loading' });
    try {
      const result = await getRecordFileText(tenantId, id);
      setTextState({ phase: 'ready', result });
    } catch (err) {
      setTextState({ phase: 'error', message: err instanceof ApiError && err.serverMessage ? err.serverMessage : 'the text could not be reached' });
    }
  }

  if (load.phase === 'loading') {
    return (
      <div class="sp">
        <BackLink label="Record" onClick={onBack} />
        <div class="sp__say">One moment…</div>
      </div>
    );
  }
  if (load.phase === 'error') {
    return (
      <div class="sp">
        <BackLink label="Record" onClick={onBack} />
        <div class="sp__say">
          The record is not reachable.
          <br />
          <span class="sp__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const { file } = load;
  const kindParts = [file.mediaClass, file.docType, sizeLabel(file.sizeBytes)].filter((x): x is string => !!x);

  return (
    <div class="sp">
      <BackLink label="Record" onClick={onBack} />
      <h1 class="sp__title">{file.title}</h1>
      {kindParts.length > 0 ? <p class="rfile__kind">{kindParts.join(' · ')}</p> : null}
      <p class="rfile__meta">
        ingested {new Date(file.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} by {file.ingestedBy}
      </p>
      {file.sourceUri || file.originalFilename ? (
        <p class="rfile__meta">
          {file.sourceUri ? (
            <a href={file.sourceUri} target="_blank" rel="noreferrer">{file.sourceUri}</a>
          ) : (
            file.originalFilename
          )}
        </p>
      ) : null}

      {file.capsule || file.summary ? <div class="rfile__body">{file.capsule ?? file.summary}</div> : null}

      <div class="sp__acts">
        {file.hasBytes ? (
          <button type="button" class="sp__act" disabled={downloadBusy} onClick={() => void download()}>
            Download
          </button>
        ) : null}
        {file.hasReadableText ? (
          <button type="button" class="sp__act" onClick={() => void toggleRead()}>
            {readOpen ? 'Hide' : 'Read here'}
          </button>
        ) : null}
      </div>
      {!file.hasBytes ? <p class="rfile__no-bytes">the original was not kept — summary only</p> : null}
      {downloadNote ? <p class="rfile__note">{downloadNote}</p> : null}

      {readOpen ? (
        textState.phase === 'loading' ? (
          <p class="rfile__read-pane">One moment…</p>
        ) : textState.phase === 'error' ? (
          <p class="rfile__note">{textState.message}</p>
        ) : textState.phase === 'ready' ? (
          <>
            <pre class="rfile__read-pane">{textState.result.text}</pre>
            {textState.result.truncated ? (
              <p class="rfile__read-truncated">
                Showing {textState.result.text.length.toLocaleString()} of {textState.result.totalChars.toLocaleString()} characters
                {file.hasBytes ? ' — the download has the whole file.' : '.'}
              </p>
            ) : null}
          </>
        ) : null
      ) : null}
    </div>
  );
}
